import { Box, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"

import {
  GL_TRANSACTION_POSTED_BY_LABELS,
  GL_TRANSACTION_ROW_TYPE_LABELS,
  GL_TRANSACTION_STATUS_LABELS,
} from "../../constants"

import { FilterChip } from "@/components/common/Filters"
import type { GlTransactionStatus } from "@/graphql/codegen/graphql"

export const TransactionsFilterChips: FC = () => {
  const navigate = useNavigate({ from: "/app/accounting/gl-transactions" })
  const currentSearch = useSearch({
    from: "/app/accounting/_layout/gl-transactions",
    strict: true,
  })

  const { transactionType, statuses = [], postedByType, dateFrom, dateTo } = currentSearch

  const hasActiveFilters = !!transactionType || statuses.length > 0 || !!postedByType || !!(dateFrom || dateTo)

  const handleRemoveTransactionType = async () => {
    await navigate({
      search: { ...currentSearch, transactionType: undefined },
      replace: true,
    })
  }

  const handleRemoveStatus = async (status: GlTransactionStatus) => {
    const updated = statuses.filter((s) => s !== status)
    await navigate({
      search: { ...currentSearch, statuses: updated.length > 0 ? updated : undefined },
      replace: true,
    })
  }

  const handleRemovePostedBy = async () => {
    await navigate({
      search: { ...currentSearch, postedByType: undefined },
      replace: true,
    })
  }

  const handleRemoveDateRange = async () => {
    await navigate({
      search: { ...currentSearch, dateFrom: undefined, dateTo: undefined, dateType: undefined },
      replace: true,
    })
  }

  if (!hasActiveFilters) return null

  return (
    <Box data-testid='transactions.filter-chips' display='flex' flexWrap='wrap' alignItems='center' gap={2}>
      <Typography variant='body1'>Filters:</Typography>

      {transactionType && (
        <FilterChip
          key={`transactions.filter-chips.type-chip-${transactionType}`}
          testId={`transactions.filter-chips.type-chip-${transactionType}`}
          label={`Type: ${GL_TRANSACTION_ROW_TYPE_LABELS[transactionType]}`}
          onDelete={handleRemoveTransactionType}
        />
      )}

      {statuses.map((status) => (
        <FilterChip
          key={`transactions.filter-chips.status-chip-${status}`}
          testId={`transactions.filter-chips.status-chip-${status}`}
          label={`Status: ${GL_TRANSACTION_STATUS_LABELS[status]}`}
          onDelete={() => handleRemoveStatus(status)}
        />
      ))}

      {postedByType && (
        <FilterChip
          key={`transactions.filter-chips.posted-by-chip-${postedByType}`}
          testId={`transactions.filter-chips.posted-by-chip-${postedByType}`}
          label={`Posted By: ${GL_TRANSACTION_POSTED_BY_LABELS[postedByType]}`}
          onDelete={handleRemovePostedBy}
        />
      )}

      {(dateFrom || dateTo) && (
        <FilterChip
          key='transactions.filter-chips.date-range-chip'
          testId='transactions.filter-chips.date-range-chip'
          label={`Period: ${dateFrom ?? "—"} – ${dateTo ?? "—"}`}
          onDelete={handleRemoveDateRange}
        />
      )}
    </Box>
  )
}
